import React, { useState } from "react";
import { Store, Loader2, Sparkles, Check, Wand2, Film, Image as ImageIcon, Megaphone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TA = (props) => (
  <textarea {...props} className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-2.5 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40" />
);

export default function StorePageBuilder({ gameId }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [p, setP] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/games/${gameId}/storepage`);
      setP({ ...data, tags: (data.tags || []).join(", ") });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      await api.post(`/games/${gameId}/storepage/apply`, {
        title: p.title, description: p.long_description,
        tags: (p.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
        category: p.genre,
      });
      toast.success("Store page applied to your game! ✨");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6" data-testid="storepage-builder">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="font-bold flex items-center gap-2"><Store className="w-4 h-4 text-[hsl(var(--primary))]" /> AI Store Page Builder</h2>
        <Button size="sm" onClick={generate} disabled={loading} className="rounded-full" data-testid="storepage-generate-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {p ? "Regenerate" : "Generate store page"}
        </Button>
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">AI writes your marketplace copy — title, description, tags, trailer script and a launch post. Edit anything, then apply it to your game.</p>
      {p && (
        <div className="space-y-3" data-testid="storepage-result">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title"><Input value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} data-testid="storepage-title" /></Field>
            <Field label="Genre / category"><Input value={p.genre} onChange={(e) => setP({ ...p, genre: e.target.value })} data-testid="storepage-genre" /></Field>
          </div>
          <Field label="Short description"><Input value={p.short_description} onChange={(e) => setP({ ...p, short_description: e.target.value })} data-testid="storepage-short" /></Field>
          <Field label="Long description"><TA rows={4} value={p.long_description} onChange={(e) => setP({ ...p, long_description: e.target.value })} data-testid="storepage-long" /></Field>
          <Field label="Tags (comma separated)"><Input value={p.tags} onChange={(e) => setP({ ...p, tags: e.target.value })} data-testid="storepage-tags" /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Info icon={Film} label="Trailer script" value={p.trailer_script} />
            <Info icon={Megaphone} label="Launch announcement" value={p.launch_announcement} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Info icon={ImageIcon} label="Cover image prompt" value={p.cover_prompt} />
            <Info icon={Wand2} label="Ideas" value={`${p.monetization_suggestion || ""}\n${p.level_pack_suggestion || ""}\n${(p.screenshot_ideas || []).join(" · ")}`} />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button onClick={apply} disabled={applying} className="rounded-full" data-testid="storepage-apply-btn">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply to game
            </Button>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Updates title, description, tags & category.</span>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">{label}</label><div className="mt-1">{children}</div></div>
);
const Info = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3">
    <div className="text-xs font-semibold flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> {label}</div>
    <div className="text-xs text-[hsl(var(--muted-foreground))] whitespace-pre-line">{value || "—"}</div>
  </div>
);
